import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { publicUrl } from "@/lib/storage";

type Product = {
  id: string;
  brand_name: string;
  product_name: string;
  category: string | null;
  price: string | null;
  image_path: string | null;
  image_paths: string[];
  product_url: string | null;
  quote: string | null;
  is_house_brand: boolean;
  published: boolean;
};

type AthleteRef = { id: string; slug: string; name: string; image_path: string | null };

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [athletes, setAthletes] = useState<AthleteRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const { data: p } = await supabase
        .from("brand_products")
        .select("*")
        .eq("id", id)
        .eq("published", true)
        .maybeSingle();
      if (!p) { setProduct(null); setLoading(false); return; }
      setProduct(p as any);
      setActiveImg((p as any).image_path ? publicUrl((p as any).image_path) : null);

      const { data: links } = await supabase
        .from("product_athletes")
        .select("athlete_id")
        .eq("product_id", id);
      const aIds = (links ?? []).map((r: any) => r.athlete_id);
      if (aIds.length) {
        const { data: aData } = await supabase
          .from("athletes")
          .select("id,slug,name,image_path")
          .in("id", aIds)
          .eq("published", true);
        setAthletes((aData ?? []) as any);
      }
      setLoading(false);
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-center">
          <h1 className="font-display text-4xl mb-4">Product not found</h1>
          <Link to="/shop" className="text-primary underline">Back to shop</Link>
        </div>
      </div>
    );
  }

  const allImgs = [
    product.image_path && publicUrl(product.image_path),
    ...(product.image_paths ?? []).map((p) => publicUrl(p)),
  ].filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="container py-12 md:py-16 grid md:grid-cols-2 gap-10">
          <div>
            <div className="aspect-square rounded-[1.5rem] overflow-hidden bg-muted relative">
              {activeImg ? (
                <img src={activeImg} alt={product.product_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-6xl text-muted-foreground">
                  {product.brand_name.charAt(0)}
                </div>
              )}
              {product.is_house_brand && (
                <span className="absolute top-3 left-3 text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full bg-primary text-primary-foreground">
                  Official
                </span>
              )}
            </div>
            {allImgs.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {allImgs.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(img)}
                    className={`w-20 h-20 rounded-xl overflow-hidden shrink-0 border-2 ${activeImg === img ? "border-primary" : "border-transparent"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">← Back to shop</Link>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
              {product.brand_name}{product.category ? ` · ${product.category}` : ""}
            </p>
            <h1 className="font-display text-4xl md:text-5xl mt-2 text-balance">{product.product_name}</h1>
            {product.price && <p className="font-display text-3xl text-primary mt-4">{product.price}</p>}
            {product.quote && (
              <blockquote className="serif italic text-lg text-muted-foreground mt-6 border-l-4 border-accent pl-4">
                "{product.quote}"
              </blockquote>
            )}

            {product.product_url ? (
              <a
                href={product.product_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-block px-8 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
              >
                Shop offsite ↗
              </a>
            ) : (
              <p className="mt-8 text-sm text-muted-foreground">External purchase link coming soon.</p>
            )}

            {athletes.length > 0 && (
              <div className="mt-10 pt-8 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  {athletes.length === 1 ? "Backed by" : "Backed by"}
                </p>
                <div className="flex flex-wrap gap-3">
                  {athletes.map((a) => (
                    <Link key={a.id} to={`/athlete/${a.slug}`} className="flex items-center gap-2 px-3 py-2 rounded-full bg-muted hover:bg-secondary hover:text-secondary-foreground transition-colors">
                      {a.image_path && (
                        <img src={publicUrl(a.image_path)!} alt="" className="w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="text-sm font-semibold">{a.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
